import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDownRight, MoveRight } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const lineVariant = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.5 + i * 0.13 },
  }),
};

export default function Hero() {
  const { t } = useLang();
  const lines = [t("hero.l1"), t("hero.l2"), t("hero.l3")];
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imageY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imageScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="section-hero"
      className="relative min-h-screen overflow-hidden pt-28 md:pt-32"
    >
      {/* Flowing organic background */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <motion.div
          animate={{ x: [0, 40, 0], y: [0, -30, 0], scale: [1, 1.1, 1] }}
          transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -right-24 top-24 h-[32rem] w-[32rem] rounded-full blur-3xl"
          style={{ background: "radial-gradient(circle, rgba(74,93,78,0.18) 0%, transparent 70%)" }}
        />
        <motion.div
          animate={{ x: [0, -30, 0], y: [0, 40, 0], scale: [1, 1.15, 1] }}
          transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -left-32 bottom-0 h-[30rem] w-[30rem] rounded-full blur-3xl"
          style={{ background: "radial-gradient(circle, rgba(184,93,67,0.16) 0%, transparent 70%)" }}
        />
        <motion.div
          animate={{ x: [0, 25, 0], y: [0, 25, 0] }}
          transition={{ duration: 26, repeat: Infinity, ease: "easeInOut" }}
          className="absolute left-1/3 top-1/2 h-[26rem] w-[26rem] rounded-full blur-3xl"
          style={{ background: "radial-gradient(circle, rgba(212,163,115,0.14) 0%, transparent 70%)" }}
        />
      </div>

      <div className="relative mx-auto grid max-w-[1400px] grid-cols-1 gap-10 px-6 md:px-10 lg:grid-cols-12">
        {/* Left: kinetic type */}
        <div className="flex flex-col justify-center lg:col-span-7">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="mb-8 flex items-center gap-3"
          >
            <span className="h-px w-10 bg-[#B85D43]" />
            <span className="overline text-[#B85D43]">
              {t("hero.badge")}
            </span>
          </motion.div>

          <h1 className="font-serif-display text-[clamp(2.75rem,8vw,7rem)] font-light leading-[0.95] tracking-tight text-[#1A2118]">
            {lines.map((line, i) => (
              <span key={i} className="mask-line">
                <motion.span
                  custom={i}
                  variants={lineVariant}
                  initial="hidden"
                  animate="visible"
                  className="block"
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 max-w-xl text-base leading-relaxed text-[#3A4536] md:text-lg"
          >
            {t("hero.subtitle")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 flex flex-wrap items-center gap-5"
          >
            <button
              data-testid="hero-cta-explore"
              onClick={() =>
                document.querySelector("#divisions")?.scrollIntoView({ behavior: "smooth" })
              }
              className="group flex items-center gap-3 rounded-full bg-[#1A2118] px-8 py-4 text-sm text-[#F4F1EB] transition-colors hover:bg-[#B85D43]"
            >
              {t("hero.explore")}
              <MoveRight
                strokeWidth={1.5}
                className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5"
              />
            </button>
            <button
              data-testid="hero-cta-contact"
              onClick={() =>
                document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" })
              }
              className="link-underline overline text-[#1A2118]"
            >
              {t("hero.contact")}
            </button>
          </motion.div>
        </div>

        {/* Right: parallax framed image */}
        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
            className="relative h-[52vh] overflow-hidden rounded-[2px] border border-[#3A4536]/10 lg:h-[76vh]"
          >
            <motion.img
              style={{ y: imageY, scale: imageScale }}
              src="https://images.unsplash.com/photo-1633509817627-5a29634475af?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxoZXJiYWwlMjBwb3dkZXIlMjBib3RhbmljYWwlMjBleHRyYWN0c3xlbnwwfHx8fDE3ODUwNjI5Nzd8MA&ixlib=rb-4.1.0&q=85"
              alt="Assorted herbal powders and botanical ingredients"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1A2118]/30 to-transparent" />
            <div className="absolute bottom-5 left-5 rounded-full bg-[#F4F1EB]/80 px-4 py-2 backdrop-blur-md">
              <span className="overline text-[#1A2118]">{t("hero.imgBadge")}</span>
            </div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="mx-auto mt-10 flex max-w-[1400px] items-center gap-2 px-6 text-[#3A4536] md:px-10"
      >
        <ArrowDownRight strokeWidth={1.5} className="h-4 w-4" />
        <span className="overline">{t("hero.scroll")}</span>
      </motion.div>
    </section>
  );
}
